import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import {
    Box,
    Button,
    Divider,
    Grid,
    IconButton,
} from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TitleWithDetail } from 'common/components';
import AccordionCard from 'common/components/accordions/AccordionCard';
import ReactHookFormInput from 'common/components/form/ReactHookFormInput';

import AutocompleteTags from './AutocompleteTags';

const ListingArray = ({
    control,
    disabled,
    getValues,
    initializeAuthEmailsForm,
    name,
    openEmailModal,
    protectedList,
    setCurrentIndex,
    setValue,
    title,
}) => {
    const { t } = useTranslation();
    const { fields, append, remove } = useFieldArray({ control, name });

    const handleNewItem = () => {
        append({
            key: '',
            name: '',
            type: protectedList ? 'protected' : 'opened',
            values: [],
        });
    };

    const handleRemove = (index) => {
        remove(index);
        setValue(name, getValues(name));
    };

    return (
        <AccordionCard
            defaultExpanded
            customTitle={
                <TitleWithDetail variant="body2">{title}</TitleWithDetail>
            }
            sxCard={{ boxShadow: 'none' }}
        >
            {fields.map((item, i) => (
                <Grid container mt={i > 0 ? 1 : 0} mb={1} key={item.id}>
                    <Grid size={{ md: 9, xs: 18 }}>
                        <ReactHookFormInput
                            control={control}
                            disabled={disabled}
                            label={t('Name')}
                            name={`${name}.${i}.name`}
                        />
                    </Grid>
                    <Grid size={{ md: 9, xs: 18 }}>
                        <ReactHookFormInput
                            control={control}
                            disabled={disabled}
                            label={t('Variable name')}
                            name={`${name}.${i}.key`}
                        />
                    </Grid>
                    <Grid size={{ xs: 18 }}>
                        <Box
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                gap: 0.5,
                                width: '100%',
                            }}
                        >
                            <Box sx={{ flexGrow: 1 }}>
                                <AutocompleteTags
                                    control={control}
                                    disabled={disabled}
                                    name={`${name}.${i}.values`}
                                />
                            </Box>
                            {protectedList && (
                                <IconButton
                                    disabled={disabled}
                                    onClick={() => {
                                        initializeAuthEmailsForm({
                                            auth_emails: getValues(
                                                `${name}.${i}.auth_emails`,
                                            ),
                                        });
                                        setCurrentIndex(i);
                                        openEmailModal(true);
                                    }}
                                >
                                    <LockIcon fontSize="small" />
                                </IconButton>
                            )}
                            <IconButton
                                disabled={disabled}
                                onClick={() => handleRemove(i)}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    </Grid>
                    <Grid size={18}>
                        <Divider />
                    </Grid>
                </Grid>
            ))}

            {fields && fields.length === 0 && (
                <Grid container mt={2}>
                    <Grid size="grow"></Grid>
                </Grid>
            )}

            {fields && fields.length >= 0 && (
                <Grid container mt={2}>
                    <Grid size={18} textAlign="right">
                        <Button
                            endIcon={<AddIcon />}
                            disabled={disabled}
                            onClick={() =>
                                handleNewItem(
                                    getValues(`${name}.${fields.length - 1}`),
                                )
                            }
                            sx={{ width: { lg: 'auto', xs: '100%' } }}
                            variant="outlined"
                        >
                            {t('Add')}
                        </Button>
                    </Grid>
                </Grid>
            )}
        </AccordionCard>
    );
};

ListingArray.propTypes = {
    control: PropTypes.object,
    disabled: PropTypes.bool,
    getValues: PropTypes.func,
    initializeAuthEmailsForm: PropTypes.func,
    name: PropTypes.string,
    openEmailModal: PropTypes.func,
    protectedList: PropTypes.bool,
    setCurrentIndex: PropTypes.func,
    setValue: PropTypes.func,
    title: PropTypes.string,
};

export default ListingArray;
